/**
 * Which responsibilities exist, and what one permission checkbox says.
 *
 * @module components/teams/team-permissions
 */

import type { PermissionCell, PermissionOption } from "@/components/teams/ProgrammePermissions";
import {
  DRAFT_BUILDER_REASON,
  isSoleLead,
  permissionReason,
  type ResponsibilityGroup,
  writeRefusal,
} from "@/components/teams/team-assignments";
import type { ProgrammeResponsibility, ProgrammeResponsibilityAssignment } from "@/server/types";

export const RESPONSIBILITY_OPTIONS: readonly {
  value: ProgrammeResponsibility;
  label: string;
}[] = [
  { value: "lead", label: "Programme Lead" },
  { value: "builder", label: "Builder" },
  { value: "reviewer", label: "Reviewer" },
  { value: "approver", label: "Approver" },
  { value: "viewer", label: "Viewer" },
];

export const DEFAULT_ALLOWED_RESPONSIBILITIES = RESPONSIBILITY_OPTIONS.map(
  (option) => option.value,
) as readonly ProgrammeResponsibility[];

export interface PermissionContext {
  responsibilities: readonly ProgrammeResponsibilityAssignment[];
  allowedResponsibilities: readonly ProgrammeResponsibility[];
  /** Why this account may not manage the team at all; null when it may. */
  refusal: string | null;
  locked: boolean;
  saving: boolean;
}

/**
 * One description per cell, so the table and the cards below `md` cannot
 * disagree about what is checked or why a box is refused.
 */
export function permissionCell(
  group: ResponsibilityGroup,
  option: PermissionOption,
  context: PermissionContext,
): PermissionCell {
  const assignment = group.assignments.find(
    (candidate) => candidate.responsibility === option.value,
  );
  const soleLead = assignment !== undefined && isSoleLead(assignment, context.responsibilities);
  const manageable =
    context.allowedResponsibilities.includes(option.value) && context.refusal === null;
  return {
    checked: Boolean(assignment),
    disabled: context.locked || !manageable || soleLead || context.saving,
    // Every cause of the refusal names itself: `ProgrammePermissions` folds
    // the reason into a disabled box's name, so a fall-through would announce
    // the action the control is about to refuse.
    reason: permissionReason({
      locked: context.locked,
      manageable,
      soleLead,
      saving: context.saving,
      assigned: assignment !== undefined,
      label: option.label,
      permittedReason: context.refusal ?? undefined,
    }),
  };
}

export interface RefusalInputs {
  locked: boolean;
  saving: boolean;
  permitted: boolean;
  staffReady: boolean;
  builderPermitted: boolean;
  builderReady: boolean;
}

/**
 * Why each write refuses, in the order the reader would hit them. A submit
 * that names no reason is a dead control.
 */
export function writeRefusals(inputs: RefusalInputs): {
  addStaffRefusal: string | null;
  assignBuilderRefusal: string | null;
} {
  return {
    addStaffRefusal: writeRefusal({
      locked: inputs.locked,
      saving: inputs.saving,
      permitted: inputs.permitted,
      ready: inputs.staffReady,
      readyReason: "Choose a staff member to add.",
    }),
    assignBuilderRefusal: writeRefusal({
      locked: inputs.locked,
      saving: inputs.saving,
      permitted: inputs.builderPermitted,
      permittedReason: DRAFT_BUILDER_REASON,
      ready: inputs.builderReady,
      readyReason: "Choose a module and the builder who will own it.",
    }),
  };
}
